import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { publicApi } from '../utils/publicApi';

const SCHEDULE_PAGE_SIZE = 10;

const Schedule = () => {
  const [activeTab, setActiveTab] = useState(null);
  const [scheduleData, setScheduleData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState([]);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [schedulePage, setSchedulePage] = useState(1);
  const scheduleSectionRef = useRef(null);
  const { t, isRTL } = useLanguage();

  const scrollScheduleToTop = () => {
    requestAnimationFrame(() => {
      scheduleSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setLoading(true);
        const data = await publicApi.getSchedule();

        if (data && data.length > 0) {
          setError(null);
          const groupedByDay = data.reduce((acc, event) => {
            const dayKey = event.day_label || '1';
            if (!acc[dayKey]) {
              acc[dayKey] = { events: [] };
            }
            acc[dayKey].events.push({
              id: event.id,
              time: event.time,
              title: event.title,
              description: event.description,
            });
            return acc;
          }, {});

          Object.keys(groupedByDay).forEach((dayKey) => {
            groupedByDay[dayKey].events.sort((a, b) => {
              const timeA = a.time ? a.time.toLowerCase() : '';
              const timeB = b.time ? b.time.toLowerCase() : '';
              return timeA.localeCompare(timeB);
            });
          });

          setScheduleData(groupedByDay);
          const dayKeys = Object.keys(groupedByDay).sort(
            (a, b) => parseInt(a) - parseInt(b)
          );
          setDays(dayKeys);
          setActiveTab((prev) =>
            prev && dayKeys.includes(prev) ? prev : dayKeys[0]
          );
        } else {
          setError(null);
          setScheduleData({});
          setDays([]);
        }
      } catch (err) {
        console.error('Error fetching schedule:', err);
        setError(err.message);
        setScheduleData({});
        setDays([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, []);

  const activeEvents = useMemo(
    () => scheduleData[activeTab]?.events || [],
    [scheduleData, activeTab]
  );

  useEffect(() => {
    setSchedulePage(1);
  }, [activeTab]);

  useEffect(() => {
    const total = Math.max(1, Math.ceil(activeEvents.length / SCHEDULE_PAGE_SIZE));
    setSchedulePage((p) => Math.min(p, total));
  }, [activeEvents.length]);

  const { paginatedEvents, totalPages, currentPage } = useMemo(() => {
    const total = Math.max(1, Math.ceil(activeEvents.length / SCHEDULE_PAGE_SIZE));
    const page = Math.min(schedulePage, total);
    const start = (page - 1) * SCHEDULE_PAGE_SIZE;
    return {
      paginatedEvents: activeEvents.slice(start, start + SCHEDULE_PAGE_SIZE),
      totalPages: total,
      currentPage: page,
    };
  }, [activeEvents, schedulePage]);

  const goSchedulePrev = () => {
    setExpandedEventId(null);
    setSchedulePage((p) => Math.max(1, p - 1));
    scrollScheduleToTop();
  };

  const goScheduleNext = () => {
    setExpandedEventId(null);
    setSchedulePage((p) => Math.min(totalPages, p + 1));
    scrollScheduleToTop();
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [h, m] = timeString.split(':');
    const hour = parseInt(h, 10);
    const suffix = hour >= 12 ? 'pm' : 'am';
    const display = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${display}:${m || '00'} ${suffix}`;
  };

  return (
    <section
      ref={scheduleSectionRef}
      id="schedule"
      className="relative overflow-hidden bg-white"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      {/* Figma: px 64, py 112, gap 80 */}
      <div className="w-full px-6 sm:px-10 lg:px-16 py-16 sm:py-20 lg:py-28 flex flex-col items-center gap-14 lg:gap-20">
        {/* Max-width wrapper */}
        <div className="w-full max-w-[1280px] flex flex-col items-center gap-14 lg:gap-20">

          {/* Header — Figma: max-w 768, title 48px 700, subtitle 16px 400 */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-[768px] flex flex-col items-center gap-6"
          >
            <h2
              className="w-full text-center text-[32px] sm:text-[40px] lg:text-[48px]"
              style={{ color: '#51516B', fontWeight: 700, lineHeight: 1.2 }}
            >
              {t('website.schedule.title')}
            </h2>
            <p
              className="w-full text-center text-[15px] sm:text-[16px]"
              style={{ color: '#51516B', fontWeight: 400, lineHeight: '24px' }}
            >
              {t('website.schedule.subtitle')}
            </p>
          </motion.div>

          {/* Schedule body */}
          <div className="w-full flex flex-col items-center gap-10 lg:gap-12">

            {/* Day pills — from DB */}
            {days.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="inline-flex items-center flex-wrap justify-center gap-1"
              >
                {days.map((day) => {
                  const isActive = activeTab === day;
                  return (
                    <button
                      key={day}
                      onClick={() => {
                        setActiveTab(day);
                        setExpandedEventId(null);
                        setSchedulePage(1);
                      }}
                      className="relative min-w-[100px] w-[120px] sm:w-[142px] py-[15px] rounded-[37px] flex items-center justify-center transition-colors duration-200"
                      style={{
                        background: isActive
                          ? 'linear-gradient(157deg, #0AB0CE 0%, #111034 100%)'
                          : 'transparent',
                      }}
                    >
                      <span
                        className="text-[15px] sm:text-[16px] font-semibold transition-colors duration-200"
                        style={{
                          color: isActive ? '#EDEAF3' : '#51516B',
                        }}
                      >
                        {t('website.schedule.dayLabel')} {day}
                      </span>
                    </button>
                  );
                })}
              </motion.div>
            )}

            {/* Events list */}
            <div className="w-full">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-12 h-12 border-4 border-[#0AB0CE]/20 border-t-[#0AB0CE] rounded-full animate-spin" />
                </div>
              ) : error ? (
                <div className="text-center py-16">
                  <p className="font-semibold text-red-600">
                    {t('website.schedule.errorPrefix')}
                  </p>
                  <p className="text-sm mt-1 text-red-500">{error}</p>
                </div>
              ) : activeEvents.length > 0 ? (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3 }}
                    className="w-full flex flex-col"
                  >
                    {paginatedEvents.map((event) => (
                      <div
                        key={event.id}
                        className="w-full border-t border-[#51516B]/30 py-6 sm:py-8"
                      >
                        {/* Desktop row: time | title (flex) | description toggle */}
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedEventId(
                              expandedEventId === event.id ? null : event.id
                            )
                          }
                          className="w-full flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 text-left rtl:text-right"
                        >
                          {/* Time */}
                          <div
                            className="shrink-0 w-auto sm:w-[96px]"
                            style={{
                              color: '#51516B',
                              fontSize: 20,
                              fontWeight: 400,
                              lineHeight: '30px',
                            }}
                          >
                            {formatTime(event.time)}
                          </div>

                          {/* Title */}
                          <div
                            className="flex-1 min-w-0"
                            style={{
                              color: '#51516B',
                              fontSize: 22,
                              fontWeight: 700,
                              lineHeight: '33.6px',
                            }}
                          >
                            <span className="text-[20px] sm:text-[24px]">
                              {event.title}
                            </span>
                          </div>

                          {/* Expand chevron */}
                          {event.description && (
                            <div className="shrink-0">
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-200 ${
                                  expandedEventId === event.id
                                    ? 'rotate-180'
                                    : ''
                                }`}
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="#51516B"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                        </button>

                        {/* Expanded description */}
                        <AnimatePresence>
                          {expandedEventId === event.id && event.description && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              <div className="pt-4 sm:pl-[128px] rtl:sm:pr-[128px] rtl:sm:pl-0">
                                <p
                                  style={{
                                    color: '#51516B',
                                    fontSize: 16,
                                    fontWeight: 400,
                                    lineHeight: '24px',
                                  }}
                                >
                                  {event.description}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              ) : null}

              {activeEvents.length > 0 && totalPages > 1 ? (
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                  <button
                    type="button"
                    onClick={goSchedulePrev}
                    disabled={currentPage <= 1}
                    className="min-h-[44px] px-4 sm:px-5 py-2 rounded-full text-[15px] sm:text-[16px] font-semibold transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      color: '#51516B',
                      border: '1px solid #51516B',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {t('website.schedule.paginationPrev')}
                  </button>
                  <span
                    className="text-[15px] sm:text-[16px] font-medium order-first sm:order-none"
                    style={{ color: '#51516B' }}
                  >
                    {t('website.schedule.paginationPage', {
                      current: String(currentPage),
                      total: String(totalPages),
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={goScheduleNext}
                    disabled={currentPage >= totalPages}
                    className="min-h-[44px] px-4 sm:px-5 py-2 rounded-full text-[15px] sm:text-[16px] font-semibold transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{
                      color: '#51516B',
                      border: '1px solid #51516B',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}
                  >
                    {t('website.schedule.paginationNext')}
                  </button>
                </div>
              ) : null}

              {!loading && !error && activeEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-[#51516B]/60">
                  <svg
                    className="w-16 h-16 mb-4 opacity-30"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-lg font-medium">
                    {t('website.schedule.noData')}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Schedule;
